// src/services/testService.js
import { db } from '../firebase.js'; // Import Firestore DB instance
import {
  collection,       // Reference a collection
  doc,              // Reference a specific document
  addDoc,           // Add a document with an auto-generated ID
  getDoc,           // Get a single document
  getDocs,          // Get multiple documents
  updateDoc,        // Update specific fields in a document
  deleteDoc,        // Delete a document
  query,            // Create a query against a collection
  where,            // Filter documents in a query
  orderBy,          // Order query results
  Timestamp,        // Firestore Timestamp object for date/time fields
  writeBatch,       // Perform multiple writes as a single atomic unit
  serverTimestamp   // Get a server-generated timestamp
} from 'firebase/firestore';

// --- Test Management Functions ---

/**
 * Creates a new test document in the 'tests' collection and its associated
 * question documents in the 'questions' subcollection using a batch write
 * for atomicity.
 *
 * @param {object} testData - Test metadata { title, dueDate (JS Date object), classroomId, questionOrder? (optional array of question IDs) }.
 * @param {string} creatorId - UID of the teacher creating the test.
 * @param {Array<object>} questionsData - Array of question objects. Each object should have:
 * { type: ('discursive'|'multipleChoice'|'associative'), text: string, order?: number, points?: number,
 * options?: string[], correctOptionIndex?: number, columnA?: string[], columnB?: string[] }.
 * @returns {Promise<string>} The ID of the newly created test document.
 * @throws {Error} If required data is missing or if the batch write fails.
 */
export const createTest = async (testData, creatorId, questionsData) => {
  // Validate essential inputs
  if (!testData || !testData.title || !testData.dueDate || !testData.classroomId || !creatorId || !questionsData || questionsData.length === 0) {
    throw new Error("Missing required data for test creation (title, dueDate, classroomId, creatorId, questionsData).");
  }

  const batch = writeBatch(db); // Initialize a batch write operation

  // 1. Prepare the main test document data
  const testsCollectionRef = collection(db, 'tests');
  const newTestRef = doc(testsCollectionRef); // Firestore auto-generates an ID for the new test document
  const testDocData = {
    title: testData.title,
    // Ensure dueDate is stored as a Firestore Timestamp
    dueDate: testData.dueDate instanceof Date ? Timestamp.fromDate(testData.dueDate) : Timestamp.now(), // Convert JS Date or use current time if invalid
    classroomId: testData.classroomId,
    creatorId: creatorId,
    createdAt: serverTimestamp(), // Use server timestamp for creation time
    updatedAt: serverTimestamp(), // Use server timestamp for last update time
    questionCount: questionsData.length, // Store question count for convenience
    // questionOrder will be set later if not provided
  };

  // 2. Prepare question documents for the subcollection
  const questionsCollectionRef = collection(newTestRef, 'questions'); // Reference the 'questions' subcollection within the new test document
  const questionIds = []; // Array to store the auto-generated IDs of the questions

  questionsData.forEach((qData, index) => {
    // Validate question data (basic example)
    if (!qData.type || !qData.text) {
        console.warn(`Skipping question at index ${index} due to missing type or text.`);
        return; // Skip invalid question data
    }
    // TODO: Add more specific validation based on question type (e.g., options for multipleChoice)

    const newQuestionRef = doc(questionsCollectionRef); // Firestore auto-generates an ID for the new question document
    questionIds.push(newQuestionRef.id); // Store the generated ID

    const questionDocData = {
      type: qData.type,
      text: qData.text,
      order: qData.order ?? index, // Use provided order, fallback to array index
      points: qData.points ?? 0, // Default points to 0 if not provided
      // Include type-specific fields only if they exist in qData
      ...(qData.type === 'multipleChoice' && { options: qData.options || [], correctOptionIndex: qData.correctOptionIndex ?? null }),
      ...(qData.type === 'associative' && { columnA: qData.columnA || [], columnB: qData.columnB || [] }),
    };
    // Add the operation to set the question document data to the batch
    batch.set(newQuestionRef, questionDocData);
  });

  // If no valid questions were processed, abort the creation
   if (questionIds.length === 0) {
       throw new Error("No valid questions provided for the test.");
   }
   // Update the question count based on valid questions added
   testDocData.questionCount = questionIds.length;


  // 3. Set the question order in the main test document if not provided explicitly
  if (!testData.questionOrder || testData.questionOrder.length !== questionIds.length) {
    testDocData.questionOrder = questionIds; // Use the order in which questions were processed
  } else {
    testDocData.questionOrder = testData.questionOrder; // Use the provided order
  }

  // 4. Add the operation to set the main test document data to the batch
  batch.set(newTestRef, testDocData);

  // 5. Commit all batched writes atomically
  try {
    await batch.commit();
    console.log(`Test created successfully with ID: ${newTestRef.id}`);
    return newTestRef.id; // Return the ID of the newly created test
  } catch (error) {
    console.error("Error committing test creation batch: ", error);
    throw new Error("Failed to create test. The operation was rolled back.");
  }
};

/**
 * Fetches a single test document by its ID from the 'tests' collection.
 *
 * @param {string} testId - The ID of the test to fetch.
 * @returns {Promise<object|null>} A promise that resolves to the test data object
 * (including its ID) or null if the test is not found.
 * @throws {Error} If the fetch operation fails.
 */
export const getTestById = async (testId) => {
  if (!testId) {
      console.warn("getTestById called with no ID.");
      return null;
  }
  try {
    const testRef = doc(db, 'tests', testId); // Create a document reference
    const docSnap = await getDoc(testRef); // Fetch the document snapshot
    // Return the document data including its ID if it exists, otherwise return null
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  } catch (error) {
    console.error(`Error fetching test ${testId}: `, error);
    throw new Error("Failed to fetch test data.");
  }
};

/**
 * Fetches all tests assigned to a specific classroom, ordered by due date (ascending).
 *
 * @param {string} classroomId - The ID of the classroom.
 * @returns {Promise<Array<object>>} A promise that resolves to an array of test objects.
 * @throws {Error} If the fetch operation fails.
 */
export const getTestsForClassroom = async (classroomId) => {
  if (!classroomId) {
      console.warn("getTestsForClassroom called with no classroomId.");
      return []; // Return empty array if no classroom ID is provided
  }
  try {
    const testsRef = collection(db, 'tests');
    // Query tests where 'classroomId' matches, order by 'dueDate'
    const q = query(testsRef, where('classroomId', '==', classroomId), orderBy('dueDate', 'asc'));
    const querySnapshot = await getDocs(q);
    // Map the results to an array of objects, including document IDs
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error(`Error fetching tests for classroom ${classroomId}: `, error);
    throw new Error("Failed to fetch classroom tests.");
  }
};

/**
 * Fetches all tests created by a specific teacher, ordered by creation date (descending).
 *
 * @param {string} teacherId - The UID of the teacher.
 * @returns {Promise<Array<object>>} A promise that resolves to an array of test objects.
 * @throws {Error} If the fetch operation fails.
 */
export const getTestsCreatedByTeacher = async (teacherId) => {
  if (!teacherId) {
      console.warn("getTestsCreatedByTeacher called with no teacherId.");
      return []; // Return empty array if no teacher ID is provided
  }
  try {
    const testsRef = collection(db, 'tests');
    // Query tests where 'creatorId' matches, order by 'createdAt' descending
    const q = query(testsRef, where('creatorId', '==', teacherId), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    // Map the results to an array of objects, including document IDs
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error(`Error fetching tests created by teacher ${teacherId}: `, error);
    throw new Error("Failed to fetch created tests.");
  }
};


/**
 * Updates specific fields of a test document in the 'tests' collection.
 * Automatically updates the 'updatedAt' timestamp.
 *
 * @param {string} testId - The ID of the test to update.
 * @param {object} dataToUpdate - An object containing the fields and new values to update (e.g., { title: "New Title", dueDate: new Date() }).
 * @returns {Promise<void>} A promise that resolves when the update is complete.
 * @throws {Error} If the update operation fails.
 */
export const updateTest = async (testId, dataToUpdate) => {
  if (!testId || !dataToUpdate || Object.keys(dataToUpdate).length === 0) {
      console.warn("updateTest called with invalid parameters.");
      return; // Exit if no ID or data provided
  }
  try {
    const testRef = doc(db, 'tests', testId); // Create a document reference
    const updatePayload = { ...dataToUpdate }; // Copy data to avoid modifying original object

    // Convert dueDate to Firestore Timestamp if it's a JS Date object
    if (updatePayload.dueDate && updatePayload.dueDate instanceof Date) {
        updatePayload.dueDate = Timestamp.fromDate(updatePayload.dueDate);
    }

    // Add the 'updatedAt' timestamp to the update payload
    updatePayload.updatedAt = serverTimestamp();

    // Perform the update operation
    await updateDoc(testRef, updatePayload);
    console.log(`Test ${testId} updated successfully.`);
  } catch (error) {
    console.error(`Error updating test ${testId}: `, error);
    throw new Error("Failed to update test.");
  }
};

/**
 * Deletes a test document from the 'tests' collection.
 * IMPORTANT: This function ONLY deletes the main test document. It DOES NOT automatically
 * delete the questions in its subcollection or any related answers in the 'answers' collection.
 * Proper cleanup requires a Cloud Function triggered by this deletion event.
 *
 * @param {string} testId - The ID of the test document to delete.
 * @returns {Promise<void>} A promise that resolves when the deletion is complete.
 * @throws {Error} If the deletion fails.
 */
export const deleteTest = async (testId) => {
  if (!testId) {
      console.warn("deleteTest called with no ID.");
      return; // Exit if no ID provided
  }
  try {
    const testRef = doc(db, 'tests', testId); // Create a document reference
    await deleteDoc(testRef); // Delete the document

    // Log a critical warning about cleanup
    console.warn(`Test document ${testId} deleted. Subcollections ('questions') and related 'answers' documents were NOT automatically deleted. Implement server-side cleanup (e.g., Cloud Function) is required.`);

    // TODO: Trigger a Cloud Function here if possible, or ensure manual/backend cleanup process exists.

  } catch (error) {
    console.error(`Error deleting test ${testId}: `, error);
    throw new Error("Failed to delete test document.");
  }
};


// --- Question Management Functions (Operating on the subcollection) ---

/**
 * Fetches all question documents from the subcollection of a specific test,
 * ordered by the 'order' field (ascending).
 *
 * @param {string} testId - The ID of the parent test document.
 * @returns {Promise<Array<object>>} A promise that resolves to an array of question objects.
 * @throws {Error} If the fetch operation fails.
 */
export const getQuestionsForTest = async (testId) => {
  if (!testId) {
      console.warn("getQuestionsForTest called with no testId.");
      return []; // Return empty array if no test ID is provided
  }
  try {
    // Reference the 'questions' subcollection within the specific test document
    const questionsRef = collection(db, 'tests', testId, 'questions');
    // Query documents ordered by the 'order' field
    const q = query(questionsRef, orderBy('order', 'asc'));
    const querySnapshot = await getDocs(q);
    // Map the results to an array of objects, including document IDs
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error(`Error fetching questions for test ${testId}: `, error);
    throw new Error("Failed to fetch test questions.");
  }
};

// Note: Functions to add, update, or delete *individual* questions within a test
// after creation are often handled by updating the entire test structure via `updateTest`
// or by managing the questions array directly in the UI and then re-creating/updating
// the questions subcollection in a batch. Adding separate functions like `addQuestionToTest`,
// `updateQuestionInTest`, `deleteQuestionFromTest` is possible but adds complexity
// to maintaining consistency (e.g., updating `questionCount` and `questionOrder` in the parent test doc).

